import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  Button, TextField, Autocomplete, Pagination,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GridOnIcon from '@mui/icons-material/GridOn';
import { Workbook as ExcelWorkbook } from 'exceljs';
import { useAuth } from 'react-oidc-context';
import { useSession, Terminal } from '../../../../core/context/SessionContext';
import { VentaApiService } from '../../../venta/infrastructure/api/VentaApiService';
import { VentaEmpaqueRecord, KpisEmpaque, Mostrador } from '../../domain/entities/VentaEmpaqueRecord';

const ROWS_PER_PAGE = 10;

interface KpiCardProps {
  label: string;
  value: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value }) => (
  <Paper
    elevation={1}
    sx={{
      flex: 1,
      minWidth: 160,
      p: 2,
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
      {label}
    </Typography>
    <Typography variant="h5" fontWeight="bold">
      {value}
    </Typography>
  </Paper>
);

export const HistorialDetallePage: React.FC = () => {
  const auth = useAuth();
  const { terminal } = useSession();
  const moneda: 'COP' | 'MXN' = terminal?.pais === 'MX' ? 'MXN' : 'COP';

  const today = new Date().toISOString().split('T')[0];
  const isFirstDateRender = useRef(true);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Filter inputs (no aplicados aún)
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [terminalFilter, setTerminalFilter] = useState('Todas');
  const [mostradorFilter, setMostradorFilter] = useState('Todo');

  // Filtros aplicados al presionar Consultar
  const [activeFilters, setActiveFilters] = useState({
    startDate: today,
    endDate: today,
    terminal: 'Todas',
    mostrador: 'Todo',
  });

  const [terminales, setTerminales] = useState<Terminal[]>([]);
  const [mostradores, setMostradores] = useState<Mostrador[]>([]);

  const [kpis, setKpis] = useState<KpisEmpaque | null>(null);
  const [rows, setRows] = useState<VentaEmpaqueRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(moneda === 'COP' ? 'es-CO' : 'es-MX', {
      style: 'currency',
      currency: moneda,
      minimumFractionDigits: 2,
    }).format(amount);

  const TASA_IVA = moneda === 'MXN' ? 0.16 : 0.19;
  const DESCRIPCIONES_CON_IVA_INCLUIDO = new Set(['TRANSPORTE - MENSAJERIA', 'TRANSPORTE - MERCANCIA']);
  const calcularTotal = (subtotalStr: string | number, descripcion: string) => {
    const base = Number.parseFloat(String(subtotalStr));
    const yaIncluye = DESCRIPCIONES_CON_IVA_INCLUIDO.has(descripcion.trim().toUpperCase());
    return formatCurrency(yaIncluye ? base : base * (1 + TASA_IVA));
  };

  const formatFechaHora = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(moneda === 'COP' ? 'es-CO' : 'es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const loadData = useCallback(async () => {
    if (!auth.user?.access_token) return;
    setIsLoading(true);
    setTotal(0);
    setRows([]);
    try {
      const terminalParam = activeFilters.terminal === 'Todas' ? undefined : activeFilters.terminal;
      const mostradorParam = activeFilters.mostrador === 'Todo' ? undefined : activeFilters.mostrador;

      const [kpisResult, detalleResult] = await Promise.all([
        VentaApiService.obtenerKpisEmpaque(
          auth.user.access_token,
          terminal?.pais ?? '',
          activeFilters.startDate,
          activeFilters.endDate
        ),
        VentaApiService.obtenerDetalleEmpaque(
          auth.user.access_token,
          terminal?.pais ?? '',
          page,
          ROWS_PER_PAGE,
          {
            startDate: activeFilters.startDate,
            endDate: activeFilters.endDate,
            terminal: terminalParam,
            mostrador: mostradorParam,
          }
        ),
      ]);

      setKpis(kpisResult);
      setRows(detalleResult.data);
      setTotal(detalleResult.total);
    } finally {
      setIsLoading(false);
    }
  }, [auth.user?.access_token, page, activeFilters, terminal?.pais]);

  useEffect(() => {
    if (!auth.user?.access_token) return;
    VentaApiService.obtenerTerminalesBD(auth.user.access_token, terminal?.pais)
      .then(setTerminales)
      .catch(() => {});
  }, [auth.user?.access_token, terminal?.pais]);

  useEffect(() => {
    if (!auth.user?.access_token) return;

    setMostradorFilter('Todo');
    setActiveFilters((prev) => ({ ...prev, mostrador: 'Todo' }));

    if (terminalFilter === 'Todas') {
      setMostradores([]);
      return;
    }

    const terminalObj = terminales.find((t) => t.nombre_terminal === terminalFilter);
    if (!terminalObj) return;

    VentaApiService.obtenerMostradoresBD(terminalObj.codigo_terminal, auth.user.access_token, terminal?.pais ?? '')
      .then(setMostradores)
      .catch(() => {});
  }, [terminalFilter, terminales, auth.user?.access_token, terminal?.pais]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
  }, [page]);

  // Debounce para fechas: espera 600ms tras el último cambio antes de buscar.
  // Se salta el primer render para no duplicar la carga inicial.
  useEffect(() => {
    if (isFirstDateRender.current) {
      isFirstDateRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setPage(1);
      setActiveFilters((prev) => ({ ...prev, startDate, endDate }));
    }, 600);
    return () => clearTimeout(timer);
  }, [startDate, endDate]);

  const handleConsultar = () => {
    setPage(1);
    setActiveFilters({
      startDate,
      endDate,
      terminal: terminalFilter,
      mostrador: mostradorFilter,
    });
  };

  const handleExportarExcel = async () => {
    if (!auth.user?.access_token) return;
    setIsExporting(true);
    try {
      const terminalParam = activeFilters.terminal === 'Todas' ? undefined : activeFilters.terminal;
      const mostradorParam = activeFilters.mostrador === 'Todo' ? undefined : activeFilters.mostrador;

      const allData = await VentaApiService.exportarDetalleEmpaque(
        auth.user.access_token,
        terminal?.pais ?? '',
        activeFilters.startDate,
        activeFilters.endDate,
        terminalParam,
        mostradorParam
      );

      const workbook = new ExcelWorkbook();
      const worksheet = workbook.addWorksheet('Ventas Empaque');
      worksheet.columns = [
        { header: 'Descripción', key: 'descripcion', width: 30 },
        { header: 'Cantidad', key: 'cantidad', width: 12 },
        { header: 'Precio Unitario', key: 'precioUnitario', width: 18 },
        { header: 'Total (c/IVA)', key: 'total', width: 18 },
        { header: 'Fecha/Hora', key: 'fechaHora', width: 20 },
        { header: 'Terminal', key: 'terminal', width: 20 },
        { header: 'Mostrador', key: 'mostrador', width: 20 },
        { header: 'Guía', key: 'guia', width: 20 },
      ];
      allData.forEach((r) => worksheet.addRow({
        descripcion: r.descripcion,
        cantidad: r.cantidad,
        precioUnitario: r.precioUnitario,
        total: DESCRIPCIONES_CON_IVA_INCLUIDO.has(r.descripcion.trim().toUpperCase())
          ? Number.parseFloat(String(r.subtotal))
          : Number.parseFloat(String(r.subtotal)) * (1 + TASA_IVA),
        fechaHora: formatFechaHora(r.fechaHora),
        terminal: r.terminal ?? '',
        mostrador: r.mostrador ?? '',
        guia: r.guia ?? '',
      }));
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `ventas-empaque-${activeFilters.startDate}_${activeFilters.endDate}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }
    if (rows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
            <Typography color="text.secondary">
              No se encontraron registros para este periodo
            </Typography>
          </TableCell>
        </TableRow>
      );
    }
    return rows.map((row, i) => (
      <TableRow key={`${row.descripcion}-${i}`} hover>
        <TableCell>{row.descripcion}</TableCell>
        <TableCell>{row.cantidad}</TableCell>
        <TableCell>{formatCurrency(Number.parseFloat(String(row.precioUnitario)))}</TableCell>
        <TableCell>{calcularTotal(row.subtotal, row.descripcion)}</TableCell>
        <TableCell>{formatFechaHora(row.fechaHora)}</TableCell>
        <TableCell>{row.guia || '—'}</TableCell>
      </TableRow>
    ));
  };

  const kpiValue = (amount: number | undefined, isCurrency: boolean) => {
    if (amount === undefined || amount === null) return '–';
    if (isCurrency) return formatCurrency(amount);
    return String(amount);
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, flexGrow: 1, minHeight: 0 }}>
      <Typography variant="h5" fontWeight="bold">
        Consulta de Ventas de Material de Empaque
      </Typography>

      {/* Panel de filtros */}
      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Periodo */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Periodo
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                minWidth: 260,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                <CalendarTodayIcon fontSize="small" />
              </Box>
              <TextField
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                variant="standard"
                size="small"
                sx={{ width: 110 }}
                slotProps={{ input: { disableUnderline: true, sx: { fontSize: '0.875rem' } } }}
              />
              <Typography color="text.secondary">–</Typography>
              <TextField
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                variant="standard"
                size="small"
                sx={{ width: 110 }}
                slotProps={{ input: { disableUnderline: true, sx: { fontSize: '0.875rem' } } }}
              />
            </Paper>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            onClick={handleConsultar}
            disabled={isLoading}
            sx={{ height: 40, px: 3, fontWeight: 600 }}
          >
            {isLoading ? <CircularProgress size={18} color="inherit" /> : 'Consultar'}
          </Button>

          <Button
            variant="outlined"
            startIcon={isExporting ? <CircularProgress size={16} /> : <GridOnIcon />}
            onClick={handleExportarExcel}
            disabled={isExporting || isLoading}
            sx={{ height: 40, px: 2, fontWeight: 600, color: 'white', backgroundColor: '#1e7e34', borderColor: '#1e7e34', '&:hover': { backgroundColor: '#155724', borderColor: '#155724' } }}
          >
            Exportar Excel
          </Button>
        </Box>
      </Paper>

      {/* KPI cards */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <KpiCard
          label="Total Ventas Empaque Hoy"
          value={kpiValue(kpis?.totalVentasEmpaqueHoy, true)}
        />
        <KpiCard
          label="Nº Ítems Vendidos Material de Empaque"
          value={kpiValue(kpis?.nItemsVendidos, false)}
        />
        <KpiCard
          label="Total Ventas Guías"
          value={kpiValue(kpis?.totalVentasGuias, true)}
        />
        <KpiCard
          label="Nº Guías Generadas"
          value={kpiValue(kpis?.nGuiasGeneradas, false)}
        />
        <KpiCard
          label="Total Ventas (Consolidado)"
          value={kpiValue(kpis?.totalConsolidado, true)}
        />
      </Box>

      {/* Tabla */}
      <Paper elevation={2} sx={{ borderRadius: 2, display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
        {/* Dropdowns Terminal / Mostrador */}
        <Box sx={{ display: 'flex', gap: 2, p: 2, pb: 0 }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 220 }}
            options={[{ nombre_terminal: 'Todas', codigo_terminal: -1 } as Terminal, ...terminales]}
            value={terminales.find(t => t.nombre_terminal === terminalFilter) ?? { nombre_terminal: 'Todas', codigo_terminal: -1 } as Terminal}
            getOptionLabel={(t) => t.codigo_terminal === -1 ? 'Todas' : `${t.nombre_terminal} (Cód: ${t.codigo_terminal})`}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.toLowerCase();
              return opts.filter(t =>
                t.nombre_terminal.toLowerCase().includes(q) ||
                String(t.codigo_terminal).includes(q)
              );
            }}
            onChange={(_, selected) => {
              const val = selected?.nombre_terminal ?? 'Todas';
              setTerminalFilter(val);
              setPage(1);
              setActiveFilters((prev) => ({ ...prev, terminal: val }));
            }}
            isOptionEqualToValue={(opt, val) => opt.codigo_terminal === val.codigo_terminal}
            disableClearable
            renderInput={(params) => <TextField {...params} label="Terminal" />}
          />

          <Autocomplete
            size="small"
            sx={{ minWidth: 200 }}
            options={[{ nombre_mostrador: 'Todo', codigo_mostrador: -1 } as Mostrador, ...mostradores]}
            value={mostradores.find(m => m.nombre_mostrador === mostradorFilter) ?? { nombre_mostrador: 'Todo', codigo_mostrador: -1 } as Mostrador}
            getOptionLabel={(m) => m.nombre_mostrador}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.toLowerCase();
              return opts.filter(m => m.nombre_mostrador.toLowerCase().includes(q));
            }}
            onChange={(_, selected) => {
              const val = selected?.nombre_mostrador ?? 'Todo';
              setMostradorFilter(val);
              setPage(1);
              setActiveFilters((prev) => ({ ...prev, mostrador: val }));
            }}
            isOptionEqualToValue={(opt, val) => opt.codigo_mostrador === val.codigo_mostrador}
            disableClearable
            renderInput={(params) => <TextField {...params} label="Mostrador" />}
          />
        </Box>

        <TableContainer ref={tableContainerRef} sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {['Descripción', 'Cantidad', 'Precio Unitario', 'Total (c/IVA)', 'Fecha/Hora', 'Guía'].map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {renderTableContent()}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Paginación — fuera del Paper para garantizar visibilidad */}
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, flexShrink: 0 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, newPage) => setPage(newPage)}
          showFirstButton
          showLastButton
          shape="rounded"
          disabled={isLoading}
          sx={{
            '& .MuiPaginationItem-root.Mui-selected': {
              backgroundColor: '#0066cc',
              color: 'white',
              '&:hover': { backgroundColor: '#0052a3' },
            },
          }}
        />
      </Box>
    </Box>
  );
};
